import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Star } from "lucide-react";
import HeroTower from "@/components/HeroTower";
import { LineReveal } from "@/components/Reveal";
import { SITE, IMAGES } from "@/lib/site";

const STATS = [
  { v: "1 Gbps", l: "Peak Speeds" },
  { v: "0", l: "Data Caps" },
  { v: "5★", l: "Local Support" },
  { v: "No", l: "Contracts" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden bg-ink-950" data-testid="hero">
      {/* Parallax Colorado background */}
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
        <img src={IMAGES.heroMesa} alt="Rural Colorado hillside under a wide sky" className="w-full h-[128%] object-cover opacity-[0.62]" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/75 to-ink-950/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-transparent to-ink-950/50" />
      </motion.div>

      {/* Silver cell tower + signal waves */}
      <HeroTower />

      <div className="absolute inset-0 z-[2] grid-lines opacity-40" />

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-32">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
          className="overline mb-6 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-cyan-500" />
          Rural Internet · {SITE.areas.slice(0, 3).join(" · ")}, CO
        </motion.p>

        <h1 className="font-display font-black tracking-tighter text-white leading-[0.88] text-[16vw] sm:text-[12vw] lg:text-[9rem]">
          <LineReveal
            lines={["LOVE YOUR", <span key="l2" className="text-aurora">INTERNET.</span>]}
            delay={0.25}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-lg text-zinc-300 leading-relaxed"
        >
          Finally enjoy buffer-free movie nights, lag-free gaming, and video calls that just work. Blazing-fast wireless internet built for the whole family in rural {SITE.state} — unlimited data, no contracts, and real local support.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link to="/survey" className="inline-flex items-center gap-2 bg-cyan-500 text-ink-950 font-display font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full btn-glow hover:bg-cyan-400" data-testid="hero-check-availability">
            Check Availability <ArrowRight size={18} />
          </Link>
          <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center gap-2 border border-white/20 text-white font-display font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full hover:border-cyan-500 hover:text-cyan-400 transition-colors" data-testid="hero-call">
            <Phone size={16} /> {SITE.phone}
          </a>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden max-w-3xl"
        >
          {STATS.map((s) => (
            <div key={s.l} className="bg-ink-900/70 backdrop-blur px-5 py-6">
              <div className="font-display font-black text-3xl text-white flex items-center gap-1">
                {s.v === "5★" ? (<><span>5</span><Star size={20} className="fill-cyan-400 text-cyan-400" /></>) : s.v}
              </div>
              <div className="font-mono-tech text-[11px] uppercase tracking-widest text-zinc-500 mt-1">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
